<template>
  <section class="card">
    <div class="page-head-row">
      <button
        type="button"
        class="back-btn"
        title="返回活动列表"
        @click="goBack"
        @mouseenter="backBtnHover = true"
        @mouseleave="backBtnHover = false"
      >
        <img
          class="back-btn-icon"
          :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
          alt="返回"
        >
      </button>
    <h2 class="head-title">{{ headTitle }}</h2>
      <button
        type="button"
        class="btn btn-primary btn-save-top"
        :disabled="saving || loading"
        @click="save"
      >
        {{ saving ? '保存中…' : '保存' }}
      </button>
    </div>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div v-if="loading" class="muted">加载中…</div>

    <div v-else class="sections">
      <div class="panel">
        <h3>基础</h3>
        <div class="field-grid-3 activity-row-status">
          <label class="field">
            <span>后台状态</span>
            <FancySelect v-model="form.statusCode" icon="●" :options="statusOptions" />
          </label>
          <label class="field">
            <span>排序</span>
            <input :value="displaySortText" disabled />
          </label>
          <label class="field">
            <span>报名名额</span>
            <input
              v-model="form.signupLimit"
              type="number"
              min="0"
              step="1"
              placeholder="留空或 0 表示不限"
            />
          </label>
        </div>
        <div class="field-grid-4 activity-row-main">
          <label class="field">
            <span>标题</span>
            <input v-model="form.title" placeholder="活动名称" />
          </label>
          <label class="field">
            <span>地址</span>
            <textarea
              ref="addressTa"
              v-model="form.address"
              class="addr-textarea addr-textarea--compact"
              rows="1"
              placeholder="活动地址"
            />
          </label>
          <label class="field">
            <span>活动开始时间</span>
            <input v-model="form.activityStartAtLocal" type="datetime-local" @click="openNativeDatePicker" />
          </label>
          <label class="field">
            <span>活动结束时间</span>
            <input v-model="form.activityEndAtLocal" type="datetime-local" @click="openNativeDatePicker" />
          </label>
        </div>
        <div class="field full">
          <span class="field-label-inline">
            <span>封面图</span>
            <input ref="coverInput" class="visually-hidden-file" type="file" accept="image/*" @change="onCover" />
            <button v-if="!form.coverUrl" type="button" class="btn btn-edit" @click.stop="pickCover">上传封面</button>
            <small v-if="form.coverUrl" class="hint-inline">点击图片即可更换</small>
          </span>
          <div class="upload-row">
            <img
              v-if="form.coverUrl"
              class="thumb thumb-clickable"
              :src="toFullUrl(form.coverUrl)"
              alt=""
              title="点击更换封面"
              @click.stop="pickCover"
            />
            <span v-else class="muted">尚未上传封面</span>
          </div>
        </div>
      </div>

      <div class="panel panel--rich">
        <h3>活动详情</h3>
        <p class="hint hint--block">
          下方为图文编辑器，可做出接近公众号推文的版式（分段、加粗、颜色、列表、引用等）。复杂海报长图也可在正文中插入图片实现。
        </p>
        <ActivityHtmlEditor v-model="form.introHtml" />
      </div>

      <div class="panel">
        <h3>报名与名单</h3>
        <label class="field full">
          <span>报名公示名单：对谁可见</span>
          <FancySelect v-model="form.signupRosterVisibility" icon="👥" :options="rosterVisOptions" />
        </label>
        <label class="form-check-row form-check-row--block">
          <input v-model="form.openToNonMember" type="checkbox" />
          <span>对非会员开放报名（关闭后仅正式会员可从活动详情在线报名）</span>
        </label>
        <div class="fee-inline-row">
          <div class="field fee-field-grow">
            <label class="fee-fee-label" for="activity-signup-fee-yuan"><span>报名费（元）</span></label>
            <div class="fee-input-row">
              <input
                id="activity-signup-fee-yuan"
                v-model.number="form.signupFeeYuan"
                type="number"
                min="0"
                step="0.01"
                placeholder="0 表示免费"
              />
              <label class="form-check-row form-check-row--fee-inline">
                <input v-model="form.requireFormalMemberFee" type="checkbox" />
                <span>正式会员需要收费</span>
        </label>
            </div>
          </div>
        </div>
        <label class="field full">
          <span>满意度问卷模板</span>
          <FancySelect
            v-model="form.satisfactionFormCode"
            icon="📋"
            :options="satisfactionTemplateOptions"
            placeholder="不启用满意度调查"
          />
          <p class="hint hint--block">活动结束且会员已签到后可填写；模板在「问卷管理」维护。</p>
        </label>
        <div class="panel panel--checkin">
          <h3>签到与现场</h3>
          <p class="hint hint--block">
            本场同时支持：<strong>用户扫签到处场所码</strong>（本人手机核验+签名）与<strong>会务签到</strong>（搜索/扫电子凭证后签名）。
            每场活动创建后会自动生成固定场所码；现场报名暗号与会务口令相互独立。
          </p>
          <label class="field full">
            <span>签到处场所码（创建/保存后生成，可下载打印）</span>
            <p class="hint hint--block venue-qr-hint">
              请<strong>打印下方小程序码</strong>张贴至签到处。参会人用<strong>微信扫一扫</strong>后，在<strong>本人手机</strong>完成第 1 步核验与第 2 步电子签名。
              现场报名暗号请<strong>口头告知</strong>未报名来宾，由其扫码后在本人手机输入（不在此展示）。
            </p>
            <div class="venue-qr-panel">
              <div v-if="venueQrLoading" class="muted">加载场所码…</div>
              <template v-else-if="venueCheckinMpPath">
                <div v-if="venueQrDataUrl" class="venue-qr-visual">
                  <img
                    :src="venueQrDataUrl"
                    alt="签到处场所码"
                    class="venue-qr-img"
                    title="点击预览大图"
                    @click="venueQrPreviewOpen = true"
                  />
                </div>
                <p v-else class="muted">二维码生成失败，请点「刷新场所码」重试</p>
                <div class="venue-qr-actions">
                  <button
                    v-if="venueQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="venueQrPreviewOpen = true"
                  >
                    预览大图
                  </button>
                  <button
                    v-if="venueQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="downloadVenueQr"
                  >
                    下载 PNG
                  </button>
                  <button type="button" class="btn btn-edit" @click="copyVenueMpPath">复制路径</button>
                  <button
                    v-if="!isNew"
                    type="button"
                    class="btn btn-edit"
                    :disabled="venueQrLoading"
                    @click="loadVenueCheckinQr"
                  >
                    刷新场所码
                  </button>
                </div>
                <code class="venue-path">{{ venueCheckinMpPath }}</code>
              </template>
              <p v-else class="muted">新建活动请先点「保存」；保存后将自动生成场所码</p>
            </div>
          </label>
          <label class="field">
            <span>现场报名暗号</span>
            <input
              v-model="form.onSiteSignupCode"
              type="password"
              autocomplete="new-password"
              placeholder="会务代报用，与会务口令、临时报名码不同"
            />
          </label>
          <label class="field full">
            <span>临时报名固定二维码（创建/保存后生成，可下载打印）</span>
            <p class="hint hint--block venue-qr-hint">
              未提前报名的来宾可<strong>微信扫一扫</strong>此<strong>小程序码</strong>，<strong>无需注册</strong>直接填写报名信息；提交后进入<strong>待审核</strong>，审核通过后可上传凭证、签名并查看胸卡。
            </p>
            <div class="venue-qr-panel">
              <div v-if="guestQrLoading" class="muted">加载临时报名码…</div>
              <template v-else-if="guestSignupMpPath">
                <div v-if="guestQrDataUrl" class="venue-qr-visual">
                  <img
                    :src="guestQrDataUrl"
                    alt="临时报名二维码"
                    class="venue-qr-img"
                    title="点击预览大图"
                    @click="guestQrPreviewOpen = true"
                  />
                </div>
                <p v-else class="muted">二维码生成失败，请点「刷新临时报名码」重试</p>
                <div class="venue-qr-actions">
                  <button
                    v-if="guestQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="guestQrPreviewOpen = true"
                  >
                    预览大图
                  </button>
                  <button
                    v-if="guestQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="downloadGuestQr"
                  >
                    下载 PNG
                  </button>
                  <button type="button" class="btn btn-edit" @click="copyGuestMpPath">复制路径</button>
                  <button
                    v-if="!isNew"
                    type="button"
                    class="btn btn-edit"
                    :disabled="guestQrLoading"
                    @click="loadGuestSignupQr"
                  >
                    刷新临时报名码
                  </button>
                </div>
                <code class="venue-path">{{ guestSignupMpPath }}</code>
              </template>
              <p v-else class="muted">新建活动请先点「保存」；保存后将自动生成临时报名码</p>
            </div>
          </label>
          <label class="field full">
            <span>活动分享小程序码（创建/保存后生成，可下载打印）</span>
            <p class="hint hint--block venue-qr-hint">
              微信扫一扫进入<strong>本场活动详情</strong>（不带邀请人）。适合海报张贴、群发推广；
              与上方「场所码 / 临时报名码」用途不同。会员在小程序内分享生成的码会带邀请归因。
            </p>
            <div class="venue-qr-panel">
              <div v-if="shareQrLoading" class="muted">加载活动分享码…</div>
              <template v-else-if="shareActivityMpPath">
                <div v-if="shareQrDataUrl" class="venue-qr-visual">
                  <img
                    :src="shareQrDataUrl"
                    alt="活动分享小程序码"
                    class="venue-qr-img"
                    title="点击预览大图"
                    @click="shareQrPreviewOpen = true"
                  />
                </div>
                <p v-else class="muted">二维码生成失败，请点「刷新活动分享码」重试</p>
                <div class="venue-qr-actions">
                  <button
                    v-if="shareQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="shareQrPreviewOpen = true"
                  >
                    预览大图
                  </button>
                  <button
                    v-if="shareQrDataUrl"
                    type="button"
                    class="btn btn-edit"
                    @click="downloadShareQr"
                  >
                    下载 PNG
                  </button>
                  <button type="button" class="btn btn-edit" @click="copyShareMpPath">复制路径</button>
                  <button
                    type="button"
                    class="btn btn-edit"
                    :disabled="shareQrLoading"
                    @click="loadActivityShareQr"
                  >
                    刷新活动分享码
                  </button>
                </div>
                <code class="venue-path">{{ shareActivityMpPath }}</code>
              </template>
              <template v-else-if="!isNew && editId">
                <p class="muted">
                  活动分享码加载失败（常见原因：服务器还在跑旧 jar，接口
                  <code>/share-wxa-code</code>
                  返回 404）。请部署含该接口的新后端后重试。
                </p>
                <div class="venue-qr-actions">
                  <button type="button" class="btn btn-edit" @click="loadActivityShareQr">重新加载活动分享码</button>
                </div>
              </template>
              <p v-else class="muted">新建活动请先点「保存」；保存后将自动生成活动分享码</p>
            </div>
          </label>
        </div>
        <div class="staff-checkin-row">
          <label class="field">
          <span>会务签到口令</span>
            <div class="password-input-wrap">
          <input
            v-model="form.checkinStaffPin"
                :type="checkinStaffPinVisible ? 'text' : 'password'"
            autocomplete="new-password"
                class="input-pin"
            placeholder="小程序「会务签到」核销用；可不填"
              />
              <button
                type="button"
                class="btn-pin-visibility"
                :title="checkinStaffPinVisible ? '隐藏口令' : '显示口令'"
                :aria-pressed="checkinStaffPinVisible ? 'true' : 'false'"
                aria-label="显示或隐藏口令"
                @click="checkinStaffPinVisible = !checkinStaffPinVisible"
              >
                <svg v-if="!checkinStaffPinVisible" class="pin-eye-svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
                  />
                </svg>
                <svg v-else class="pin-eye-svg" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75C20.27 7.61 16 4.5 12 4.5c-.88 0-1.73.09-2.54.26l2.36 2.36c.57-.23 1.18-.36 1.83-.36zM2.71 2.29 1.39 3.61l2.73 2.73C2.73 7.61 1 12 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l3.53 3.53 1.41-1.41L4.12 4.12 2.71 2.29zM7.53 9.8l1.55 1.55c-.09.46-.14.93-.14 1.4 0 2.76 2.24 5 5 5 .47 0 .94-.05 1.39-.14l1.66 1.66c-.97.51-2.07.8-3.25.8-3.86 0-7-3.14-7-7 0-1.18.29-2.28.8-3.25zM11.84 9.02l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                  />
                </svg>
              </button>
            </div>
          </label>
          <label class="field">
            <span>会务签到人员</span>
            <FancyMultiSelect
              v-model="staffPickMemberIds"
              icon="👤"
              :options="staffMemberMultiSelectOptions"
              searchable
              search-placeholder="搜索姓名、单位、手机…"
              placeholder="不指定（不勾选任何人）"
              :disabled="staffMemberPickLoading"
          />
        </label>
        </div>
        <p v-if="staffMemberPickLoadError" class="error">{{ staffMemberPickLoadError }}</p>
        <p v-else class="muted staff-pick-hint">
          从已注册小程序账号中<strong>勾选一名或多名</strong>，下拉每项前有复选框；所选人员均可在活动详情打开「会务签到」，与是否报名本场无关。不勾选任何人则小程序不展示该入口。
        </p>
      </div>

      <div class="panel">
        <h3>活动微信群</h3>
        <p class="hint hint--block">
          上传本场活动微信群二维码。会员活动费订单<strong>已支付</strong>（或免费订单已完成）后，可在小程序「我的订单」「我的活动」中查看并长按识别入群。
        </p>
        <div class="field full">
          <span class="field-label-inline">
            <span>微信群二维码</span>
            <input ref="wechatGroupQrInput" class="visually-hidden-file" type="file" accept="image/*" @change="onWechatGroupQr" />
            <button v-if="!form.wechatGroupQrUrl" type="button" class="btn btn-edit" @click.stop="pickWechatGroupQr">上传二维码</button>
            <button v-if="form.wechatGroupQrUrl" type="button" class="btn btn-edit" @click.stop="clearWechatGroupQr">清除</button>
          </span>
          <div class="upload-row">
            <img
              v-if="form.wechatGroupQrUrl"
              class="thumb thumb-clickable"
              :src="toFullUrl(form.wechatGroupQrUrl)"
              alt=""
              title="点击更换二维码"
              @click.stop="pickWechatGroupQr"
            />
            <span v-else class="muted">尚未上传微信群二维码</span>
          </div>
        </div>
      </div>

      <div class="panel">
        <h3>胸卡打印</h3>
        <p class="hint hint--block">
          在「模板管理 → 胸卡模版」上传<strong>已含背景与 Logo 的 PNG</strong>；此处启用后，签到成功可在后台/浏览器签到台打印，会员可在小程序查看并保存胸卡。
        </p>
        <label class="form-check-row">
          <input v-model="form.enableBadgePrint" type="checkbox" />
          <span>本场启用胸卡</span>
        </label>
        <label v-if="form.enableBadgePrint" class="field full">
          <span>胸卡模版</span>
          <FancySelect
            v-model="form.badgeTemplateId"
            icon="🪪"
            :options="badgeTemplateOptions"
            placeholder="请选择胸卡模版"
          />
        </label>
      </div>

      <div class="panel">
        <h3>活动报名 · 活动信息收集</h3>
        <p class="hint hint--block">
          小程序里「姓名、手机、单位、职务」四项由系统固定，这里只配置<strong>额外要问的问题</strong>（例如意向考察团、饮食偏好等）。下表只需填写中文名称与题型，内部字段名由系统自动生成。
        </p>

        <div class="collect-simple">
          <table class="collect-table">
            <thead>
              <tr>
                <th style="width:26%">前台显示名称</th>
                <th style="width:18%">类型</th>
                <th style="width:8%">必填</th>
                <th>选项（下拉/多选时填写，<strong>每行一条</strong>）</th>
                <th style="width:120px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in collectRows" :key="'cr-' + idx">
                <td><input v-model.trim="row.label" class="collect-inp" placeholder="如 意向考察团" /></td>
                <td>
                  <select v-model="row.fieldType" class="collect-inp collect-select">
                    <option v-for="o in collectFieldTypeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </td>
                <td class="td-center"><input v-model="row.required" type="checkbox" /></td>
                <td>
                  <textarea
                    v-model="row.optionsLines"
                    class="collect-opt-ta"
                    rows="3"
                    placeholder="如：A线&#10;B线"
                    spellcheck="false"
                  />
                </td>
                <td class="td-actions">
                  <button type="button" class="btn-linkish" :disabled="idx === 0" @click="moveCollectRow(idx, -1)">上移</button>
                  <button
                    type="button"
                    class="btn-linkish"
                    :disabled="idx >= collectRows.length - 1"
                    @click="moveCollectRow(idx, 1)"
                  >下移</button>
                  <button type="button" class="btn-linkish btn-linkish--danger" @click="removeCollectRow(idx)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
          <button type="button" class="btn btn-edit collect-add" @click="addCollectRow">＋ 添加一项</button>
          <p v-if="!collectRows.length" class="muted collect-empty">当前未配置额外问题；保存后小程序本场只有基本信息四项。</p>
        </div>
      </div>

      <div class="panel panel--rich">
        <h3>活动咨询（小程序详情底部）</h3>
        <p class="hint hint--block">
          只作用于当前活动：用户在小程序该活动详情页点「咨询」时，弹出此处编辑的正文。若留空，将提示「尚未配置」并引导联系秘书处。与「我的 → 联系我们」全站页面无关。
        </p>
        <div class="field-grid-2 contact-fields">
          <label class="field">
            <span>联系人</span>
            <input v-model="form.contactName" maxlength="64" placeholder="如：王老师" />
          </label>
          <label class="field">
            <span>联系电话</span>
            <input
              v-model="form.contactPhone"
              maxlength="32"
              placeholder="用于订阅消息，如 13800138000"
            />
          </label>
        </div>
        <p class="hint hint--block">
          「联系电话」会写入报名成功订阅消息；请填写可接通的手机号。咨询正文仍可另行编写说明。
        </p>
        <ActivityHtmlEditor v-model="form.consultHtml" />
      </div>

      <div class="panel">
        <label class="field full">
          <span>备注（仅后台可见）</span>
          <input v-model="form.remark" placeholder="可不填；会务口令请填上方「会务签到口令」，勿写进备注" />
        </label>
      </div>

      <div class="foot-actions">
        <button type="button" class="btn btn-primary" :disabled="saving" @click="save">
          {{ saving ? '保存中…' : '保存' }}
        </button>
      </div>
    </div>

    <div v-if="venueQrPreviewOpen && venueQrDataUrl" class="venue-qr-mask" @click.self="venueQrPreviewOpen = false">
      <div class="venue-qr-dialog" @click.stop>
        <h4 class="venue-qr-dialog-title">签到处场所码</h4>
        <img :src="venueQrDataUrl" alt="签到处场所码" class="venue-qr-dialog-img" />
        <p class="hint">请下载或截图后打印张贴；参会人扫码后在本人手机完成签到与签名。</p>
        <div class="venue-qr-dialog-actions">
          <button type="button" class="btn btn-edit" @click="downloadVenueQr">下载 PNG</button>
          <button type="button" class="btn btn-primary" @click="venueQrPreviewOpen = false">关闭</button>
        </div>
      </div>
    </div>
    <div v-if="guestQrPreviewOpen && guestQrDataUrl" class="venue-qr-mask" @click.self="guestQrPreviewOpen = false">
      <div class="venue-qr-dialog" @click.stop>
        <h4 class="venue-qr-dialog-title">临时报名二维码</h4>
        <img :src="guestQrDataUrl" alt="临时报名二维码" class="venue-qr-dialog-img" />
        <p class="hint">请下载或截图后提供给现场来宾扫码报名（无需注册）。</p>
        <div class="venue-qr-dialog-actions">
          <button type="button" class="btn btn-edit" @click="downloadGuestQr">下载 PNG</button>
          <button type="button" class="btn btn-primary" @click="guestQrPreviewOpen = false">关闭</button>
        </div>
      </div>
    </div>
    <div v-if="shareQrPreviewOpen && shareQrDataUrl" class="venue-qr-mask" @click.self="shareQrPreviewOpen = false">
      <div class="venue-qr-dialog" @click.stop>
        <h4 class="venue-qr-dialog-title">活动分享小程序码</h4>
        <img :src="shareQrDataUrl" alt="活动分享小程序码" class="venue-qr-dialog-img" />
        <p class="hint">微信扫一扫进入本场活动详情；可下载打印或转发推广。</p>
        <div class="venue-qr-dialog-actions">
          <button type="button" class="btn btn-edit" @click="downloadShareQr">下载 PNG</button>
          <button type="button" class="btn btn-primary" @click="shareQrPreviewOpen = false">关闭</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'
import FancySelect from '@/components/FancySelect.vue'
import FancyMultiSelect from '@/components/FancyMultiSelect.vue'
import ActivityHtmlEditor from '@/components/ActivityHtmlEditor.vue'
import {
  createAdminCmsActivity,
  fetchAdminVenueCheckinQr,
  fetchAdminGuestSignupQr,
  fetchAdminActivityShareWxaQr,
  getAdminCmsActivity,
  getAdminCmsActivityGeocode,
  getAdminMembers,
  getBadgeTemplates,
  getSatisfactionTemplates,
  unwrapAdminMembersResponse,
  updateAdminCmsActivity,
  uploadImage,
  toMediaUrl,
} from '@/api'

function parseApiNumber(v) {
  if (v == null) return NaN
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN
  const s = String(v).trim()
  if (!s) return NaN
  const n = Number(s)
  return Number.isFinite(n) ? n : NaN
}

/**
 * 腾讯选点 iframe：仅合法坐标才带 coord，否则易报「传递的经纬度不合法」。
 * 注意：Number(null)===0，若保存时把「未填」当成 0 写入库，会出现 (0, 实数经度) 等组合，腾讯侧会判非法。
 */
function isUsableNavCoord(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false
  if (Math.abs(lat) < 1e-7 && Math.abs(lng) < 1e-7) return false
  // 任一分量接近 0 多为脏数据（null→0、只填了一半等），选点组件不接受
  if (Math.abs(lat) < 1e-4 || Math.abs(lng) < 1e-4) return false
  return true
}

/** 表单/接口上的纬度或经度：空串与 null 不参与 Number(null)=0 的陷阱 */
function parseNavFormComponent(v) {
  if (v == null) return NaN
  if (typeof v === 'string' && !v.trim()) return NaN
  const n = Number(v)
  return Number.isFinite(n) ? n : NaN
}

/** 备注字段历史上可能混入系统行；展示与保存时剔除，口令等请用上方独立字段 */
function stripSystemLinesFromRemark(text) {
  const s = text == null ? '' : String(text)
  if (!s.trim()) return ''
  const lines = s.split(/\r?\n/)
  const kept = lines.filter((line) => {
    const t = line.trim()
    if (!t) return true
    if (/^CHECKIN_STAFF_PIN\s*:/i.test(t)) return false
    if (/^CHECKIN_MODE\s*:/i.test(t)) return false
    if (/^ON_SITE_SIGNUP_CODE\s*:/i.test(t)) return false
    if (/^VENUE_CHECKIN_SIG\s*:/i.test(t)) return false
    if (/^FORM_CODE\s*:/i.test(t)) return false
    if (/^SIGNUP_FORM_CODE\s*:/i.test(t)) return false
    return true
  })
  return kept.join('\n').replace(/\n+$/, '').trim()
}

/** 活动信息收集：内部 fieldKey，表格不展示，保存时写入 JSON */
function newCollectFieldKey() {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

function normalizeStaffPickMemberIds(arr) {
  const s = new Set()
  for (const x of arr || []) {
    const n = Number(x)
    if (Number.isFinite(n) && n > 0) s.add(n)
  }
  return Array.from(s).sort((a, b) => a - b)
}

function toDatetimeLocalValue(isoStr) {
  if (!isoStr) return ''
  const s = String(isoStr).trim()
  if (!s) return ''
  return s.length >= 16 ? s.slice(0, 16) : s
}

function formatCollectJsonForEditor(raw) {
  if (raw == null) return ''
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw, null, 2)
    } catch {
      return ''
    }
  }
  const s = String(raw).trim()
  if (!s) return ''
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}

function validateSignupActivityCollectJsonText(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  let root
  try {
    root = JSON.parse(s)
  } catch {
    throw new Error('活动信息收集须为合法 JSON')
  }
  if (!Array.isArray(root)) {
    throw new Error('活动信息收集须为 JSON 数组')
  }
  for (let i = 0; i < root.length; i += 1) {
    const n = root[i]
    if (!n || typeof n !== 'object' || Array.isArray(n)) {
      throw new Error('活动信息收集数组元素须为对象')
    }
    const fk = String(n.fieldKey || n.field_key || '').trim()
    if (!fk) throw new Error('活动信息收集每项须包含 fieldKey')
    const label = String(n.label || n.fieldLabel || n.field_label || '').trim()
    if (!label) throw new Error(`活动信息收集每项须包含 label（字段 ${fk}）`)
    const ft = String(n.fieldType || n.field_type || '').trim()
    if (!ft) throw new Error(`活动信息收集每项须包含 fieldType（字段 ${fk}）`)
  }
  return s
}

function emptyForm() {
  return {
    title: '',
    coverUrl: '',
    wechatGroupQrUrl: '',
    activityStartAtLocal: '',
    activityEndAtLocal: '',
    address: '',
    navLat: null,
    navLng: null,
    introHtml: '',
    signupLimit: '',
    sortOrder: 0,
    statusCode: 'ENABLED',
    remark: '',
    signupCount: 0,
    signupRosterVisibility: 'NONE',
    signupFeeYuan: 0,
    consultHtml: '',
    contactName: '',
    contactPhone: '',
    checkinStaffPin: '',
    checkinMode: 'STAFF_SCAN_ATTENDEE',
    onSiteSignupCode: '',
    signupActivityCollectJson: '',
    satisfactionFormCode: '',
    requireFormalMemberFee: false,
    openToNonMember: true,
    enableBadgePrint: false,
    badgeTemplateId: '',
  }
}

export default {
  name: 'ActivityEditView',
  components: { FancySelect, FancyMultiSelect, ActivityHtmlEditor },
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      okMsg: '',
      form: emptyForm(),
      collectRows: [],
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      rosterVisOptions: [
        {
          value: 'NONE',
          label: '① 不开放 — 无人可看公示名单',
        },
        {
          value: 'PUBLIC',
          label: '② 公开 — 任何人可看（含未登录访客）',
        },
        {
          value: 'REGISTERED_ONLY',
          label: '③ 仅本场已报名 — 须已登录且本场有有效报名',
        },
      ],
      staffMemberPickRows: [],
      /** 会务签到入口：可多选会员 id；空数组表示不开放入口 */
      staffPickMemberIds: [],
      staffMemberPickLoadError: '',
      staffMemberPickLoading: false,
      /** 会务签到口令是否明文显示 */
      checkinStaffPinVisible: false,
      checkinModeOptions: [
        { value: 'STAFF_SCAN_ATTENDEE', label: '会务扫会员码（搜索/扫凭证 + 工作人员手机签名）' },
        { value: 'USER_SCAN_VENUE', label: '用户扫签到处场所码（本人手机核验 + 签名）' },
      ],
      venueCheckinMpPath: '',
      venueQrDataUrl: '',
      venueQrPreviewOpen: false,
      venueQrLoading: false,
      guestSignupMpPath: '',
      guestQrDataUrl: '',
      guestQrPreviewOpen: false,
      guestQrLoading: false,
      shareActivityMpPath: '',
      shareQrDataUrl: '',
      shareQrPreviewOpen: false,
      shareQrLoading: false,
      badgeTemplateRows: [],
      satisfactionTemplateRows: [],
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
    }
  },
  computed: {
    badgeTemplateOptions() {
      const rows = Array.isArray(this.badgeTemplateRows) ? this.badgeTemplateRows : []
      return rows
        .filter((r) => String(r.statusCode || '').toUpperCase() === 'ENABLED')
        .map((r) => ({
          value: String(r.id),
          label: `${r.templateName || '模版'}（${this.badgeLayoutLabel(r.layoutType)}）`,
        }))
    },
    satisfactionTemplateOptions() {
      const opts = [{ value: '', label: '不启用' }]
      for (const r of this.satisfactionTemplateRows || []) {
        if (String(r.statusCode || '').toUpperCase() !== 'ENABLED') continue
        opts.push({ value: String(r.code || ''), label: `${r.name || r.code}` })
      }
      return opts
    },
    routeId() {
      return this.$route.params.id
    },
    isNew() {
      return !this.routeId || this.routeId === 'new'
    },
    editId() {
      if (this.isNew) return null
      const n = Number(this.routeId)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    fromHot() {
      return String(this.$route?.query?.fromHot || '') === '1'
    },
    hotTitle() {
      return String(this.$route?.query?.hotTitle || '').trim()
    },
    headTitle() {
      if (this.fromHot && this.hotTitle) return this.hotTitle
      if (this.isNew) return '新建活动'
      return '编辑活动'
    },
    displaySortText() {
      if (this.isNew) return '自动分配（当前最大排序 + 1）'
      const n = Number(this.form.sortOrder)
      return Number.isFinite(n) ? String(n) : '0'
    },
    staffMemberMultiSelectOptions() {
      const rows = Array.isArray(this.staffMemberPickRows) ? this.staffMemberPickRows : []
      const opts = rows.map((x) => ({ value: x.memberId, label: x.optionLabel }))
      const seen = new Set(rows.map((r) => r.memberId))
      const ids = normalizeStaffPickMemberIds(this.staffPickMemberIds)
      for (const id of ids) {
        if (!seen.has(id)) {
          opts.push({
            value: id,
            label: `会员 #${id}（当前列表未包含，可取消勾选或保存保留）`,
          })
          seen.add(id)
        }
      }
      return opts
    },
    collectFieldTypeOptions() {
      return [
        { value: 'TEXT', label: '单行文字' },
        { value: 'SELECT', label: '下拉单选' },
        { value: 'CHECKBOX', label: '多选勾选' },
        { value: 'CHECKBOX_GROUP', label: '多选勾选（同多选）' },
      ]
    },
  },
  watch: {
    routeId() {
      this.boot()
    },
  },
  mounted() {
    this.boot()
  },
  methods: {
    hydrateCollectRowsFromFormJson() {
      const s = String(this.form.signupActivityCollectJson || '').trim()
      if (!s) {
        this.collectRows = []
        return
      }
      let arr
      try {
        arr = JSON.parse(s)
      } catch {
        this.collectRows = []
        return
      }
      if (!Array.isArray(arr)) {
        this.collectRows = []
        return
      }
      this.collectRows = arr
        .map((x) => this.collectRowFromApiItem(x))
        .filter((r) => r && r.fieldKey && r.label)
    },
    collectRowFromApiItem(x) {
      if (!x || typeof x !== 'object') return null
      let fieldKey = String(x.fieldKey || x.field_key || '').trim()
      let label = String(x.label || x.fieldLabel || x.field_label || '').trim()
      if (!fieldKey && label) fieldKey = newCollectFieldKey()
      if (!label && fieldKey) label = fieldKey
      if (!fieldKey || !label) return null
      const fieldType = String(x.fieldType || x.field_type || 'TEXT').trim().toUpperCase() || 'TEXT'
      const required = !!(x.required === true || x.required === 1 || x.requiredField === true || x.required_field === 1)
      let optionsLines = ''
      const oj = x.optionsJson != null ? x.optionsJson : x.options_json
      if (oj != null && String(oj).trim()) {
        try {
          const parsed = typeof oj === 'string' ? JSON.parse(oj) : oj
          if (Array.isArray(parsed)) {
            optionsLines = parsed
              .map((it) => {
                if (it && typeof it === 'object') return String(it.label ?? it.value ?? '').trim()
                return String(it ?? '').trim()
              })
              .filter(Boolean)
              .join('\n')
          }
        } catch {
          optionsLines = ''
        }
      }
      if (!optionsLines && Array.isArray(x.options)) {
        optionsLines = x.options.map((it) => String(it ?? '').trim()).filter(Boolean).join('\n')
      }
      return { fieldKey, label, fieldType, required, optionsLines }
    },
    syncSimpleCollectToFormJson() {
      const rows = this.collectRows || []
      const arr = []
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i]
        const label = String(r.label || '').trim()
        const optionsLinesRaw = String(r.optionsLines || '').trim()
        if (!label) {
          if (!optionsLinesRaw && !r.required) continue
          throw new Error(`「活动信息收集」第 ${i + 1} 行：请填写「前台显示名称」（不需要的空行请删除）`)
        }
        let fieldKey = String(r.fieldKey || '')
          .trim()
          .replace(/\s+/g, '_')
        if (!fieldKey) fieldKey = newCollectFieldKey()
        let fieldType = String(r.fieldType || 'TEXT').trim().toUpperCase() || 'TEXT'
        const allowed = ['TEXT', 'SELECT', 'CHECKBOX', 'CHECKBOX_GROUP']
        if (!allowed.includes(fieldType)) fieldType = 'TEXT'
        const o = { fieldKey, label, fieldType, required: !!r.required }
        const lines = String(r.optionsLines || '')
          .split(/\r?\n/)
          .map((x) => x.trim())
          .filter(Boolean)
        if (
          lines.length &&
          (fieldType === 'SELECT' || fieldType === 'CHECKBOX' || fieldType === 'CHECKBOX_GROUP')
        ) {
          o.optionsJson = JSON.stringify(lines)
        }
        arr.push(o)
      }
      this.form.signupActivityCollectJson = arr.length ? JSON.stringify(arr, null, 2) : ''
    },
    addCollectRow() {
      this.collectRows.push({
        fieldKey: newCollectFieldKey(),
        label: '',
        fieldType: 'TEXT',
        required: false,
        optionsLines: '',
      })
    },
    removeCollectRow(idx) {
      this.collectRows.splice(idx, 1)
    },
    moveCollectRow(idx, delta) {
      const j = idx + delta
      if (j < 0 || j >= this.collectRows.length) return
      const copy = [...this.collectRows]
      const t = copy[idx]
      copy[idx] = copy[j]
      copy[j] = t
      this.collectRows = copy
    },
    goBack() {
      this.$router.push('/content/activities')
    },
    extractStaffPickIdsFromAdminDto(raw) {
      if (!raw || typeof raw !== 'object') return []
      const a = raw.staffCheckinMemberIds != null ? raw.staffCheckinMemberIds : raw.staff_checkin_member_ids
      if (Array.isArray(a)) return normalizeStaffPickMemberIds(a)
      const j = raw.staffCheckinMemberIdsJson != null ? raw.staffCheckinMemberIdsJson : raw.staff_checkin_member_ids_json
      if (j != null && String(j).trim()) {
        try {
          const p = typeof j === 'string' ? JSON.parse(j) : j
          return Array.isArray(p) ? normalizeStaffPickMemberIds(p) : []
        } catch {
          return []
        }
      }
      return []
    },
    buildStaffMemberPickRows(list) {
      const out = []
      for (const r of Array.isArray(list) ? list : []) {
        if (!r || typeof r !== 'object') continue
        const mid = Number(r.id != null ? r.id : r.memberId != null ? r.memberId : r.member_id)
        if (!Number.isFinite(mid) || mid <= 0) continue
        const name = String(
          r.nickname != null
            ? r.nickname
            : r.registerName != null
              ? r.registerName
              : r.register_name != null
                ? r.register_name
                : '',
        ).trim()
        const company = String(
          r.companyName != null
            ? r.companyName
            : r.company_name != null
              ? r.company_name
              : r.registerCompanyName != null
                ? r.registerCompanyName
                : '',
        ).trim()
        const mobile = String(r.mobile != null ? r.mobile : r.registerMobile != null ? r.registerMobile : '').trim()
        const displayName = name || `会员 #${mid}`
        const meta = [company, mobile].filter((x) => String(x || '').trim()).join(' · ')
        const optionLabel = meta ? `${displayName} — ${meta}` : displayName
        out.push({ memberId: mid, displayName, meta, optionLabel })
      }
      return out.sort((a, b) => String(a.displayName).localeCompare(String(b.displayName), 'zh-CN'))
    },
    async loadStaffMemberPickList() {
      this.staffMemberPickLoadError = ''
      this.staffMemberPickLoading = true
      try {
        const raw = await getAdminMembers({})
        const rows = unwrapAdminMembersResponse(raw)
        this.staffMemberPickRows = this.buildStaffMemberPickRows(rows)
      } catch (e) {
        this.staffMemberPickRows = []
        this.staffMemberPickLoadError = e.message || '加载注册会员列表失败'
      } finally {
        this.staffMemberPickLoading = false
      }
    },
    toFullUrl(url) {
      const u = (url || '').trim()
      if (!u) return ''
      if (u.startsWith('data:')) return u
      return toMediaUrl(u)
    },
    pickCover() {
      if (this._pickBusy) return
      this._pickBusy = true
      this.$refs.coverInput?.click()
      window.setTimeout(() => { this._pickBusy = false }, 400)
    },
    pickWechatGroupQr() {
      if (this._pickBusy) return
      this._pickBusy = true
      this.$refs.wechatGroupQrInput?.click()
      window.setTimeout(() => { this._pickBusy = false }, 400)
    },
    clearWechatGroupQr() {
      this.form.wechatGroupQrUrl = ''
    },
    async onCover(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片 URL')
        this.form.coverUrl = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },
    async onWechatGroupQr(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data.url || ''
        if (!url) throw new Error('未获取到图片 URL')
        this.form.wechatGroupQrUrl = url
      } catch (err) {
        this.errorMsg = err.message || '上传失败'
      }
      e.target.value = ''
    },
    openNativeDatePicker(e) {
      const input = e && e.target
      if (input && typeof input.showPicker === 'function') {
        try {
          input.showPicker()
        } catch {
          // ignore: some browsers require user-gesture constraints
        }
      }
    },
    async trySilentGeocodeFromAddressForSave() {
      const q = String(this.form.address || '').trim()
      if (!q) return
      const la = Number(this.form.navLat)
      const lo = Number(this.form.navLng)
      if (isUsableNavCoord(la, lo)) return
      try {
        const res = await getAdminCmsActivityGeocode(q)
        const lat = res != null && res.lat != null ? Number(res.lat) : NaN
        const lng = res != null && res.lng != null ? Number(res.lng) : NaN
        if (isUsableNavCoord(lat, lng)) {
          this.form.navLat = lat
          this.form.navLng = lng
        }
      } catch {
        /* 静默失败：仍保存文字地址 */
      }
    },
    normApi(raw) {
      const endIso = raw.activityEndAt || raw.activity_end_at || ''
      return {
        title: raw.title || '',
        coverUrl: raw.coverUrl || raw.cover_url || '',
        wechatGroupQrUrl: raw.wechatGroupQrUrl || raw.wechat_group_qr_url || '',
        activityStartAtLocal: toDatetimeLocalValue(raw.activityStartAt || raw.activity_start_at || ''),
        activityEndAtLocal: toDatetimeLocalValue(endIso),
        address: (() => {
          const legacyCity = String(raw.city || '').trim()
          const legacyVenue = String(raw.venue || '').trim()
          const address = String(raw.address || '').trim()
          if (address) return address
          return [legacyCity, legacyVenue].filter(Boolean).join(' · ')
        })(),
        navLat: (() => {
          const la = parseApiNumber(raw.navLat != null ? raw.navLat : raw.nav_lat)
          const lo = parseApiNumber(raw.navLng != null ? raw.navLng : raw.nav_lng)
          return isUsableNavCoord(la, lo) ? la : null
        })(),
        navLng: (() => {
          const la = parseApiNumber(raw.navLat != null ? raw.navLat : raw.nav_lat)
          const lo = parseApiNumber(raw.navLng != null ? raw.navLng : raw.nav_lng)
          return isUsableNavCoord(la, lo) ? lo : null
        })(),
        introHtml: raw.introHtml || raw.intro_html || '',
        signupLimit: raw.signupLimit != null ? Number(raw.signupLimit) : raw.signup_limit != null ? Number(raw.signup_limit) : '',
        signupCount: raw.signupCount != null ? Number(raw.signupCount) : raw.signup_count != null ? Number(raw.signup_count) : 0,
        sortOrder: raw.sortOrder != null ? raw.sortOrder : raw.sort_order != null ? raw.sort_order : 0,
        statusCode: raw.statusCode || raw.status_code || 'ENABLED',
        remark: stripSystemLinesFromRemark(raw.remark || ''),
        signupRosterVisibility: raw.signupRosterVisibility || raw.signup_roster_visibility || 'NONE',
        signupFeeYuan: (() => {
          const c =
            raw.signupFeeCent != null
              ? Number(raw.signupFeeCent)
              : raw.signup_fee_cent != null
                ? Number(raw.signup_fee_cent)
                : 0
          const n = Number.isFinite(c) ? Math.max(0, c) / 100 : 0
          return Math.round(n * 100) / 100
        })(),
        consultHtml: raw.consultHtml != null ? raw.consultHtml : raw.consult_html != null ? raw.consult_html : '',
        contactName:
          raw.contactName != null
            ? String(raw.contactName)
            : raw.contact_name != null
              ? String(raw.contact_name)
              : '',
        contactPhone:
          raw.contactPhone != null
            ? String(raw.contactPhone)
            : raw.contact_phone != null
              ? String(raw.contact_phone)
              : '',
        checkinStaffPin: raw.checkinStaffPin != null ? String(raw.checkinStaffPin) : raw.checkin_staff_pin != null ? String(raw.checkin_staff_pin) : '',
        checkinMode:
          raw.checkinMode != null
            ? String(raw.checkinMode)
            : raw.checkin_mode != null
              ? String(raw.checkin_mode)
              : 'STAFF_SCAN_ATTENDEE',
        onSiteSignupCode:
          raw.onSiteSignupCode != null
            ? String(raw.onSiteSignupCode)
            : raw.on_site_signup_code != null
              ? String(raw.on_site_signup_code)
              : '',
        signupActivityCollectJson: formatCollectJsonForEditor(
          raw.signupActivityCollectJson != null
            ? raw.signupActivityCollectJson
            : raw.signup_activity_collect_json,
        ),
        satisfactionFormCode:
          raw.satisfactionFormCode != null
            ? String(raw.satisfactionFormCode)
            : raw.satisfaction_form_code != null
              ? String(raw.satisfaction_form_code)
              : '',
        requireFormalMemberFee: (() => {
          const r = raw.requireFormalMemberFee != null ? raw.requireFormalMemberFee : raw.require_formal_member_fee
          return r === true || r === 1 || r === '1'
        })(),
        openToNonMember: (() => {
          const r = raw.openToNonMember != null ? raw.openToNonMember : raw.open_to_non_member
          if (r === false || r === 0 || r === '0') return false
          return true
        })(),
        enableBadgePrint: (() => {
          const r = raw.enableBadgePrint != null ? raw.enableBadgePrint : raw.enable_badge_print
          return r === true || r === 1 || r === '1'
        })(),
        badgeTemplateId: (() => {
          const id = raw.badgeTemplateId != null ? raw.badgeTemplateId : raw.badge_template_id
          const n = Number(id)
          return Number.isFinite(n) && n > 0 ? String(n) : ''
        })(),
      }
    },
    buildPayload() {
      const toApiDateTime = (loc) => {
        const t = String(loc || '').trim()
        if (!t) return null
        return t.length === 16 ? `${t}:00` : t
      }
      let activityEndAt = null
      const loc = (this.form.activityEndAtLocal || '').trim()
      if (loc) {
        activityEndAt = loc.length === 16 ? `${loc}:00` : loc
      }
      const activityStartAt = toApiDateTime(this.form.activityStartAtLocal)
      const asLabel = (v) => String(v || '').trim().replace('T', ' ')
      const startLabel = asLabel(this.form.activityStartAtLocal)
      const endLabel = asLabel(this.form.activityEndAtLocal)
      const dateRange = startLabel || endLabel ? `${startLabel || '未设置'} ~ ${endLabel || '未设置'}` : null
      return {
        title: (this.form.title || '').trim(),
        coverUrl: (this.form.coverUrl || '').trim(),
        dateRange,
        activityStartAt,
        activityEndAt,
        address: (this.form.address || '').trim() || null,
        ...(() => {
          const la = parseNavFormComponent(this.form.navLat)
          const lo = parseNavFormComponent(this.form.navLng)
          const ok = isUsableNavCoord(la, lo)
          return { navLat: ok ? la : null, navLng: ok ? lo : null }
        })(),
        introHtml: this.form.introHtml || null,
        signupLimit: (() => {
          if (this.form.signupLimit === '' || this.form.signupLimit == null) return null
          const n = Number(this.form.signupLimit)
          return !Number.isFinite(n) || n <= 0 ? null : n
        })(),
        signupCount: Number(this.form.signupCount || 0),
        sortOrder: this.isNew ? null : this.form.sortOrder,
        statusCode: this.form.statusCode || 'ENABLED',
        remark: stripSystemLinesFromRemark(this.form.remark || '') || null,
        signupRosterVisibility: (this.form.signupRosterVisibility || 'NONE').trim(),
        signupFeeCent: (() => {
          const y = Number(this.form.signupFeeYuan)
          if (!Number.isFinite(y) || y <= 0) return 0
          return Math.min(2147483647, Math.round(y * 100))
        })(),
        consultHtml: this.form.consultHtml || null,
        contactName: (this.form.contactName || '').trim(),
        contactPhone: (this.form.contactPhone || '').trim(),
        checkinStaffPin: (this.form.checkinStaffPin || '').trim() || null,
        onSiteSignupCode: (this.form.onSiteSignupCode || '').trim() || null,
        signupActivityCollectJson: validateSignupActivityCollectJsonText(this.form.signupActivityCollectJson),
        satisfactionFormCode: (this.form.satisfactionFormCode || '').trim() || null,
        requireFormalMemberFee: !!this.form.requireFormalMemberFee,
        openToNonMember: !!this.form.openToNonMember,
        staffCheckinAudience: 'MP_ADMIN_FLAG',
        staffCheckinMemberIdsJson: JSON.stringify(normalizeStaffPickMemberIds(this.staffPickMemberIds)),
        staffCheckinAllowLegacyMpAdmin: false,
        enableBadgePrint: !!this.form.enableBadgePrint,
        badgeTemplateId: (() => {
          const n = Number(this.form.badgeTemplateId)
          return Number.isFinite(n) && n > 0 ? n : null
        })(),
        wechatGroupQrUrl: (this.form.wechatGroupQrUrl || '').trim() || null,
      }
    },
    badgeLayoutLabel(t) {
      const u = String(t || '').toUpperCase()
      if (u === 'PORTRAIT') return '竖版'
      if (u === 'SQUARE') return '方形'
      return '横版'
    },
    async loadBadgeTemplates() {
      try {
        this.badgeTemplateRows = (await getBadgeTemplates('ENABLED')) || []
      } catch {
        this.badgeTemplateRows = []
      }
    },
    async loadSatisfactionTemplates() {
      try {
        this.satisfactionTemplateRows = (await getSatisfactionTemplates()) || []
      } catch {
        this.satisfactionTemplateRows = []
      }
    },
    async renderVenueQrImage() {
      // 已改由后端返回微信小程序码；保留空实现避免旧调用报错
    },
    qrDataUrlFromAdmin(d) {
      if (!d || typeof d !== 'object') return ''
      const b64 = d.qrBase64 || d.qr_base64 || ''
      if (!b64) return ''
      const s = String(b64).trim()
      if (!s) return ''
      if (s.startsWith('data:')) return s
      const mime = String(d.qrMime || d.qr_mime || 'image/png').trim() || 'image/png'
      return `data:${mime};base64,${s}`
    },
    async loadVenueCheckinQr() {
      if (this.isNew || !this.editId) return
      this.venueQrLoading = true
      this.venueQrDataUrl = ''
      try {
        const d = await fetchAdminVenueCheckinQr(this.editId)
        this.venueCheckinMpPath = d && (d.mpPath || d.mp_path) ? String(d.mpPath || d.mp_path) : ''
        this.venueQrDataUrl = this.qrDataUrlFromAdmin(d)
        const err = d && (d.qrError || d.qr_error) ? String(d.qrError || d.qr_error).trim() : ''
        if (!this.venueQrDataUrl && err) {
          this.errorMsg = err
        }
      } catch (e) {
        this.venueCheckinMpPath = ''
        this.venueQrDataUrl = ''
        this.errorMsg = e.message || '场所码加载失败'
      } finally {
        this.venueQrLoading = false
      }
    },
    async renderGuestQrImage() {
      // 已改由后端返回微信小程序码
    },
    async loadGuestSignupQr() {
      if (this.isNew || !this.editId) return
      this.guestQrLoading = true
      this.guestQrDataUrl = ''
      try {
        const d = await fetchAdminGuestSignupQr(this.editId)
        this.guestSignupMpPath = d && (d.mpPath || d.mp_path) ? String(d.mpPath || d.mp_path) : ''
        this.guestQrDataUrl = this.qrDataUrlFromAdmin(d)
        const err = d && (d.qrError || d.qr_error) ? String(d.qrError || d.qr_error).trim() : ''
        if (!this.guestQrDataUrl && err) {
          this.errorMsg = err
        }
      } catch (e) {
        this.guestSignupMpPath = ''
        this.guestQrDataUrl = ''
        this.errorMsg = e.message || '临时报名码加载失败'
      } finally {
        this.guestQrLoading = false
      }
    },
    async loadActivityShareQr() {
      if (this.isNew || !this.editId) return
      this.shareQrLoading = true
      this.shareQrDataUrl = ''
      try {
        const d = await fetchAdminActivityShareWxaQr(this.editId)
        this.shareActivityMpPath = d && (d.mpPath || d.mp_path) ? String(d.mpPath || d.mp_path) : ''
        this.shareQrDataUrl = this.qrDataUrlFromAdmin(d)
        const err = d && (d.qrError || d.qr_error) ? String(d.qrError || d.qr_error).trim() : ''
        if (!this.shareQrDataUrl && err) {
          this.errorMsg = err
        }
      } catch (e) {
        this.shareActivityMpPath = ''
        this.shareQrDataUrl = ''
        this.errorMsg = e.message || '活动分享码加载失败'
      } finally {
        this.shareQrLoading = false
      }
    },
    copyShareMpPath() {
      const t = String(this.shareActivityMpPath || '').trim()
      if (!t) return
      const done = () => {
        this.okMsg = '已复制小程序路径'
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(t).then(done).catch(() => {
          this.errorMsg = '复制失败，请手动选择下方路径复制'
        })
        return
      }
      this.errorMsg = '当前浏览器不支持一键复制，请手动选择下方路径'
    },
    downloadShareQr() {
      if (!this.shareQrDataUrl) return
      const a = document.createElement('a')
      a.download = `activity-${this.editId || 'share'}-detail-qr.png`
      a.href = this.shareQrDataUrl
      a.click()
    },
    copyGuestMpPath() {
      const t = String(this.guestSignupMpPath || '').trim()
      if (!t) return
      const done = () => {
        this.okMsg = '已复制小程序路径'
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(t).then(done).catch(() => {
          this.errorMsg = '复制失败，请手动选择下方路径复制'
        })
        return
      }
      this.errorMsg = '当前浏览器不支持一键复制，请手动选择下方路径'
    },
    downloadGuestQr() {
      if (!this.guestQrDataUrl) return
      const a = document.createElement('a')
      a.download = `activity-${this.editId || 'guest'}-signup-qr.png`
      a.href = this.guestQrDataUrl
      a.click()
    },
    copyVenueMpPath() {
      const t = String(this.venueCheckinMpPath || '').trim()
      if (!t) return
      const done = () => {
        this.okMsg = '已复制小程序路径'
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(t).then(done).catch(() => {
          this.errorMsg = '复制失败，请手动选择下方路径复制'
        })
        return
      }
      this.errorMsg = '当前浏览器不支持一键复制，请手动选择下方路径'
    },
    downloadVenueQr() {
      if (!this.venueQrDataUrl) return
      const aid = this.editId || 'activity'
      const a = document.createElement('a')
      a.href = this.venueQrDataUrl
      a.download = `venue-checkin-${aid}.png`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    async boot() {
      this.errorMsg = ''
      this.okMsg = ''
      this.checkinStaffPinVisible = false
      if (this.isNew) {
        this.form = emptyForm()
        this.collectRows = []
        this.staffMemberPickRows = []
        this.staffPickMemberIds = []
        this.staffMemberPickLoadError = ''
        this.loading = false
        await this.loadStaffMemberPickList()
        await this.loadBadgeTemplates()
        await this.loadSatisfactionTemplates()
        return
      }
      if (!this.editId) {
        this.errorMsg = '无效的活动 ID'
        this.loading = false
        return
      }
      this.loading = true
      try {
        const raw = await getAdminCmsActivity(this.editId)
        this.form = { ...emptyForm(), ...this.normApi(raw) }
        this.hydrateCollectRowsFromFormJson()
        this.staffPickMemberIds = this.extractStaffPickIdsFromAdminDto(raw)
        await this.loadStaffMemberPickList()
        await this.loadBadgeTemplates()
        await this.loadSatisfactionTemplates()
        await this.loadVenueCheckinQr()
        await this.loadGuestSignupQr()
        await this.loadActivityShareQr()
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
        this.form = emptyForm()
        this.collectRows = []
        this.staffMemberPickRows = []
        this.staffPickMemberIds = []
        this.staffMemberPickLoadError = ''
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.errorMsg = ''
      this.okMsg = ''
      if (!(this.form.title || '').trim()) {
        this.errorMsg = '请填写标题'
        return
      }
      if (!(this.form.activityEndAtLocal || '').trim()) {
        this.errorMsg = '请填写活动结束时间，系统据此自动关闭报名'
        return
      }
      if (this.form.signupLimit !== '' && this.form.signupLimit != null) {
        const lim = Number(this.form.signupLimit)
        if (!Number.isFinite(lim) || lim < 0 || !Number.isInteger(lim)) {
          this.errorMsg = '报名名额须为空（不限）或非负整数'
          return
        }
      }
      try {
        this.syncSimpleCollectToFormJson()
      } catch (e) {
        this.errorMsg = e.message || '请检查「活动信息收集」表格'
        return
      }
      if (this.form.enableBadgePrint && !String(this.form.badgeTemplateId || '').trim()) {
        this.errorMsg = '已启用胸卡，请选择胸卡模版'
        return
      }
      this.saving = true
      try {
        await this.trySilentGeocodeFromAddressForSave()
        const payload = this.buildPayload()
        if (this.isNew) {
          const created = await createAdminCmsActivity(payload)
          const id = created && created.id != null ? created.id : null
          if (id == null) throw new Error('创建成功但未返回 ID')
          this.okMsg = '已创建'
          await this.$router.replace(`/content/activities/edit/${id}`)
        } else {
          await updateAdminCmsActivity(this.editId, payload)
          this.okMsg = '已保存'
          await this.boot()
        }
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  max-width: 100%;
  box-sizing: border-box;
}
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.back-btn:hover {
  transform: translateX(-1px);
}
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
.page-head-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  margin-bottom: 10px;
}
.head-title {
  margin: 0;
  font-size: 18px;
  flex: 1 1 160px;
  min-width: 0;
  line-height: 1.3;
}
.btn-save-top {
  margin-left: auto;
  flex-shrink: 0;
  height: 36px;
  padding: 0 16px;
}
.btn-back-top {
  padding: 6px 10px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-shrink: 0;
}
h2 {
  margin: 0 0 10px;
  font-size: 18px;
}
.error {
  color: #d92d20;
}
.ok {
  color: #067647;
}
.muted {
  color: #98a2b3;
}
.hint {
  display: block;
  color: #98a2b3;
  font-size: 12px;
  margin-top: 6px;
  line-height: 1.45;
}
.hint--block {
  margin-bottom: 10px;
}
.sections {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.panel {
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 14px;
  background: #fbfcff;
}
.panel h3 {
  margin: 0 0 12px;
  font-size: 14px;
  color: #101828;
}
.field-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.field-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.activity-row-status {
  margin-bottom: 10px;
}
.activity-row-status .field {
  margin-bottom: 0;
}
.field-grid-4 {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}
.field-grid-4 .field {
  margin-bottom: 0;
}
.activity-row-main {
  margin-bottom: 10px;
}
@media (max-width: 900px) {
  .field-grid-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 540px) {
  .field-grid-4 {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 720px) {
  .field-grid-2,
  .field-grid-3 {
    grid-template-columns: 1fr;
  }
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #344054;
  margin-bottom: 10px;
  min-width: 0;
}
.field.full {
  grid-column: span 2;
}
@media (max-width: 720px) {
  .field.full {
    grid-column: auto;
  }
}
.field > span {
  font-weight: 700;
}
.field-label-inline {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.hint-inline {
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
}
.field input:not([type="checkbox"]):not([type="radio"]):not([type="file"]) {
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 0 10px;
  font-size: 13px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  line-height: normal;
}
.field .addr-textarea--compact {
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.25;
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  height: 40px;
  resize: vertical;
  overflow-y: auto;
  font-family: inherit;
}
.field input:focus,
.field .addr-textarea--compact:focus {
  outline: none;
  border-color: #667085;
  box-shadow: 0 0 0 3px rgba(102, 112, 133, 0.15);
}
.field input::placeholder {
  color: #98a2b3;
}
.field input:focus::placeholder {
  color: #c0c7d4;
}
.upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.thumb {
  width: 200px;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}
.thumb-clickable {
  cursor: pointer;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.foot-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}
.visually-hidden-file {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
}
.panel--rich {
  min-width: 0;
}
.addr-textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  padding: 10px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: #101828;
  resize: vertical;
}
.addr-textarea:focus {
  outline: none;
  border-color: #667085;
  box-shadow: 0 0 0 3px rgba(102, 112, 133, 0.15);
}
.btn-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.form-check-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  font-size: 14px;
  color: #344054;
  line-height: 1.45;
  cursor: pointer;
}
.form-check-row input {
  margin-top: 3px;
  flex-shrink: 0;
}
.fee-inline-row {
  margin-bottom: 10px;
}
.fee-inline-row .fee-field-grow {
  margin-bottom: 0;
}
.fee-fee-label {
  display: block;
  cursor: default;
}
.fee-fee-label span {
  font-weight: 700;
}
.fee-input-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  min-width: 0;
}
.fee-input-row > input {
  flex: 1 1 160px;
  min-width: 120px;
}
.form-check-row--fee-inline {
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  flex: 0 0 auto;
  align-items: center;
  cursor: pointer;
}
.form-check-row--fee-inline input[type='checkbox'] {
  margin-top: 0;
}
.form-check-row--fee-inline span {
  white-space: nowrap;
}
.staff-checkin-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
  margin-bottom: 6px;
}
.staff-checkin-row .field {
  margin-bottom: 0;
}
@media (max-width: 720px) {
  .staff-checkin-row {
    grid-template-columns: 1fr;
  }
}
.password-input-wrap {
  position: relative;
  width: 100%;
}
.input-pin {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 0 40px 0 10px;
  font-size: 13px;
  min-width: 0;
  height: 40px;
  min-height: 40px;
  line-height: normal;
}
.input-pin:focus {
  outline: none;
  border-color: #667085;
  box-shadow: 0 0 0 3px rgba(102, 112, 133, 0.15);
}
.btn-pin-visibility {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  height: 34px;
  width: 34px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  color: #667085;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-pin-visibility:hover {
  color: #101828;
  background: #f2f4f7;
}
.pin-eye-svg {
  width: 18px;
  height: 18px;
  display: block;
}
.collect-simple {
  overflow-x: auto;
}
.collect-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 560px;
}
.collect-table th,
.collect-table td {
  border: 1px solid #eaecf0;
  padding: 8px;
  vertical-align: top;
  text-align: left;
}
.collect-table thead th {
  background: #f9fafb;
  color: #344054;
  font-weight: 700;
}
.collect-inp,
.collect-select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 0 10px;
  font-size: 13px;
  height: 40px;
  min-height: 40px;
  line-height: normal;
}
.collect-select {
  background: #fff;
}
.collect-opt-ta {
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.35;
  resize: vertical;
}
.td-center {
  text-align: center;
}
.td-actions {
  white-space: normal;
}
.btn-linkish {
  display: inline-block;
  margin-right: 8px;
  margin-bottom: 4px;
  padding: 0;
  border: none;
  background: none;
  color: #175cd3;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.btn-linkish:disabled {
  color: #c0c7d4;
  cursor: not-allowed;
  text-decoration: none;
}
.btn-linkish--danger {
  color: #d92d20;
}
.collect-add {
  margin-top: 10px;
}
.collect-empty {
  margin-top: 8px;
  font-size: 13px;
}
.staff-pick-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.45;
}
.venue-qr-hint {
  margin-top: 4px;
}
.venue-qr-panel {
  margin-top: 8px;
  padding: 12px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #f9fafb;
}
.venue-qr-visual {
  display: inline-block;
  padding: 8px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e4e7ec;
}
.venue-qr-img {
  display: block;
  width: 200px;
  height: 200px;
  cursor: zoom-in;
}
.venue-qr-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.venue-path {
  display: block;
  margin-top: 10px;
  padding: 8px 10px;
  font-size: 11px;
  word-break: break-all;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  color: #475467;
}
.venue-qr-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}
.venue-qr-dialog {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.18);
}
.venue-qr-dialog-title {
  margin: 0 0 12px;
  font-size: 16px;
}
.venue-qr-dialog-img {
  display: block;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
}
.venue-qr-dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;
}
</style>
